/**
 * BotTriagePanel — UI do bot de triagem no Portal Parceiro (Story E9.2.3).
 *
 * Renderiza:
 * - Pergunta atual (state.current_menu.prompt)
 * - Botões pra cada opção (mutation answer)
 * - Botão "Falar com humano" sempre disponível (mutation skip)
 * - Breadcrumb visual do caminho percorrido (state.path)
 * - Loading enquanto mutation em voo (botões disabled)
 *
 * Renderiza nada se:
 * - state == null (sem triagem engatada)
 * - state.status !== "ACTIVE" (terminal — mensagem de confirmação já
 *   está no chat como SYSTEM msg)
 *
 * Acessibilidade:
 * - role="region" + aria-label.
 * - Cada botão tem aria-label completo (resolve leitor de tela ler
 *   "1.2 Erro no pagamento" em vez de só o número).
 * - "Falar com humano" tem aria-label="Pular triagem e falar com um atendente humano".
 *
 * UX:
 * - Painel destacado com borda emerald (mesma paleta do AtendimentoPage).
 * - Não scroll embutido — fica acima do composer.
 * - Click numa opção desabilita TODOS os botões temporariamente (evita
 *   double-click acidental que mandaria 2 answers em sequência).
 */

import { Loader2, MessageSquareDashed, UserRound } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAnswerBotTriage, useBotTriageState, useSkipBotTriage } from "@/hooks/useBotTriage";
import { cn } from "@/lib/utils";

interface BotTriagePanelProps {
  sessionId: string;
}

export function BotTriagePanel({ sessionId }: BotTriagePanelProps) {
  const stateQuery = useBotTriageState(sessionId);
  const answerMutation = useAnswerBotTriage(sessionId);
  const skipMutation = useSkipBotTriage(sessionId);

  const state = stateQuery.data ?? null;
  const busy = answerMutation.isPending || skipMutation.isPending;

  // Nada a renderizar quando:
  // - sem triagem engatada (state null)
  // - status terminal (ROUTED/FALLBACK)
  // - sem menu atual (defesa em depth — backend deveria garantir)
  if (!state || state.status !== "ACTIVE" || !state.current_menu) {
    return null;
  }

  const menu = state.current_menu;
  const path = state.path;

  return (
    <div
      role="region"
      aria-label="Bot de triagem"
      className={cn("border-t border-b border-emerald-200 bg-emerald-50/60", "px-4 py-3 space-y-3")}
      data-testid="bot-triage-panel"
    >
      <div className="flex items-start gap-2">
        <div className="rounded-full bg-emerald-100 p-1.5 flex-shrink-0">
          <MessageSquareDashed className="h-4 w-4 text-emerald-700" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          {path.length > 0 && (
            <div className="text-xs text-emerald-700/80 mb-1" data-testid="bot-triage-path">
              {path.map((id, idx) => (
                <span key={`${id}-${idx}`}>
                  {idx > 0 && <span className="mx-1">›</span>}
                  <span className="font-mono">{id}</span>
                </span>
              ))}
            </div>
          )}
          <p className="text-sm font-medium text-emerald-900" data-testid="bot-triage-prompt">
            {menu.prompt}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {menu.options.map((opt) => (
          <Button
            key={opt.id}
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => answerMutation.mutate({ option_id: opt.id })}
            aria-label={opt.label}
            data-testid={`bot-triage-option-${opt.id}`}
            className="justify-start text-left whitespace-normal h-auto py-2"
          >
            {answerMutation.isPending && answerMutation.variables?.option_id === opt.id ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            <span className="break-words">{opt.label}</span>
          </Button>
        ))}
      </div>

      <div className="pt-2 border-t border-emerald-200/60">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={busy}
          onClick={() => skipMutation.mutate()}
          aria-label="Pular triagem e falar com um atendente humano"
          data-testid="bot-triage-skip"
          className="text-emerald-800 hover:bg-emerald-100/80"
        >
          {skipMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <UserRound className="h-4 w-4 mr-2" />
          )}
          Falar com humano
        </Button>
        {(answerMutation.isError || skipMutation.isError) && (
          <span className="ml-3 text-xs text-red-700" role="alert" data-testid="bot-triage-error">
            Erro ao processar resposta. Tente novamente.
          </span>
        )}
      </div>
    </div>
  );
}

export default BotTriagePanel;
